import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export type ReviewReplyStatus =
  | 'pending_draft'
  | 'draft_ready'
  | 'scheduled'
  | 'posted'
  | 'failed'
  | 'skipped'
  | 'held_manual';

export type ReviewReplyMode = 'autopilot' | 'guided';
export type ReviewReplyPatientAddressing = 'first_name' | 'no_name' | 'title_last_name';
export type ReviewReplyFormality = 'formal' | 'professional' | 'warm_casual';

export interface Review {
  id: string;
  locationId: string;
  googleReviewId: string;
  reviewerName: string | null;
  reviewerPhotoUrl: string | null;
  rating: number;
  comment: string | null;
  reviewCreateTime: string;
  reviewUpdateTime: string;
  existingReplyText: string | null;
  existingReplyTime: string | null;
  draftText: string | null;
  draftedAt: string | null;
  status: ReviewReplyStatus;
  scheduledPostAt: string | null;
  postedReplyText: string | null;
  postedAt: string | null;
  failureReason: string | null;
  hipaaFlags: string[];
  hipaaPassed: boolean | null;
  editedByHuman: boolean;
  repairAttempts: number;
  archetype: string | null;
}

export interface ReviewSettings {
  mode: ReviewReplyMode | null;
  signature: string | null;
  suggestedSignature: string | null;
  patientAddressing: ReviewReplyPatientAddressing;
  formality: ReviewReplyFormality;
  voiceNotes: string | null;
  autoDraftThreshold: number;
  aeoEnabled: boolean;
  captureReasonStepEnabled: boolean;
  configuredAt: string | null;
}

export interface ReputationOverview {
  rating: number;
  total: number;
  new30d: number;
  prev30d: number;
  replyRate: number;
  notResponded: number;
  distribution: Record<string, number>;
  themes: { label: string; count: number }[];
  themesComputedAt: string | null;
  benchmark: { avgRating: number; avgReviews: number } | null;
}

export interface ReviewStats {
  total: number;
  notResponded: number;
  lastSyncedAt: string | null;
  lastSyncNewCount: number | null;
}

export interface ReviewPage {
  data: Review[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface ReviewListResponse extends ReviewPage {
  success: boolean;
}

export const reviewsApi = {
  getReviews: (
    locationId: string,
    opts: { responded?: boolean; page?: number; limit?: number } = {},
  ): Promise<ReviewPage> =>
    apiClient
      .get<ReviewListResponse>('/reviews', {
        params: {
          locationId,
          ...(opts.responded !== undefined ? { responded: opts.responded } : {}),
          page: opts.page ?? 1,
          limit: opts.limit ?? 20,
        },
      })
      .then((r) => ({ data: r.data.data, meta: r.data.meta })),

  getStats: (locationId: string) =>
    apiClient
      .get<ApiResponse<ReviewStats>>('/reviews/stats', { params: { locationId } })
      .then((r) => r.data.data),

  getSettings: (locationId: string) =>
    apiClient
      .get<ApiResponse<ReviewSettings>>('/reviews/settings', { params: { locationId } })
      .then((r) => r.data.data),

  updateSettings: (input: {
    locationId: string;
    signature?: string;
    patientAddressing?: ReviewReplyPatientAddressing;
    formality?: ReviewReplyFormality;
    voiceNotes?: string | null;
    autoDraftThreshold?: number;
    aeoEnabled?: boolean;
    captureReasonStepEnabled?: boolean;
  }) =>
    apiClient
      .patch<ApiResponse<ReviewSettings>>('/reviews/settings', input)
      .then((r) => r.data.data),

  getReputation: (locationId: string) =>
    apiClient
      .get<ApiResponse<ReputationOverview>>('/reviews/reputation', { params: { locationId } })
      .then((r) => r.data.data),

  reclusterThemes: (locationId: string) =>
    apiClient
      .post<ApiResponse<ReputationOverview>>('/reviews/recluster', { locationId })
      .then((r) => r.data.data),

  regenerate: (id: string) =>
    apiClient.post<ApiResponse<Review>>(`/reviews/${id}/regenerate`).then((r) => r.data.data),

  post: (id: string, input: { locationId: string; editedText?: string }) =>
    apiClient.post<ApiResponse<Review>>(`/reviews/${id}/post`, input).then((r) => r.data.data),

  skip: (id: string) =>
    apiClient.post<ApiResponse<Review>>(`/reviews/${id}/skip`).then((r) => r.data.data),

  sync: (locationId: string) =>
    apiClient
      .post<ApiResponse<{ queued: boolean }>>('/reviews/sync', { locationId })
      .then((r) => r.data.data),

  regenerateAll: (locationId: string) =>
    apiClient
      .post<ApiResponse<{ queued: boolean }>>('/reviews/regenerate-all', { locationId })
      .then((r) => r.data.data),
};
